import { useEffect, useReducer } from 'react';
import type { Editor } from '@tiptap/react';
import type { RichTextEditorProps } from '@/shared/ui/rich-text-editor/types';
import { richTextToolbarStyles } from '@/shared/ui/rich-text-editor/styles';
import {
  BlockControls,
  HistoryControls,
  ListControls,
  MarksControls,
} from './format-controls';
import { HeadingMenu } from './heading-menu';
import { ImageInvoker } from './image-invoker';
import { LinkPopover } from './link-popover';
import { TableInsert } from './table-insert';
import { TableToolbar } from './table-toolbar';
import { Divider } from './toolbar-controls';

/**
 * Re-renders the toolbar whenever the editor commits a transaction.
 *
 * `isActive` and `can` are read during render, so without a subscription the
 * pressed states would only refresh when the host re-rendered for some other
 * reason — the toolbar would show the marks of the previous cursor position.
 */
function useEditorTransactions(editor: Editor) {
  const [, bump] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    const handler = () => bump();
    editor.on('transaction', handler);
    return () => {
      editor.off('transaction', handler);
    };
  }, [editor]);
}

type ToolbarProps = {
  editor: Editor;
  isDisabled: boolean;
  renderImageSource?: RichTextEditorProps['renderImageSource'];
};

/**
 * The control row above the editing surface.
 *
 * Controls are DISABLED rather than hidden when the surface cannot accept them:
 * a toolbar whose buttons move around as the cursor enters a list is a toolbar
 * the operator has to re-learn. The one exception is the image button, which is
 * absent when the host supplies no image source.
 */
export function Toolbar({ editor, isDisabled, renderImageSource }: ToolbarProps) {
  useEditorTransactions(editor);

  return (
    <div className={richTextToolbarStyles.stack}>
      <div role="group" aria-label="Formatting" className={richTextToolbarStyles.row}>
        <HistoryControls editor={editor} isDisabled={isDisabled} />
        <MarksControls editor={editor} isDisabled={isDisabled} />
        <HeadingMenu editor={editor} isDisabled={isDisabled} />
        <ListControls editor={editor} isDisabled={isDisabled} />
        <BlockControls editor={editor} isDisabled={isDisabled} />
        <Divider />
        <TableInsert editor={editor} isDisabled={isDisabled} />
        {renderImageSource ? (
          <ImageInvoker
            editor={editor}
            isDisabled={isDisabled}
            renderImageSource={renderImageSource}
          />
        ) : null}
        <LinkPopover editor={editor} isDisabled={isDisabled} />
      </div>
      <TableToolbar editor={editor} isDisabled={isDisabled} />
    </div>
  );
}
